'use client'

import { useTranslations } from 'next-intl'
import { memo, useCallback, useMemo } from 'react'
import { useGetCitiesQuery } from '@/api/cities/get-cities-query'
import { ICity, ICountry } from '@/api/cities/types/cities-types'
import { InteractiveCitiesSearchListContainer } from '@/features/cities/cities-finder/interactive-cities-search-list-container'
import { CitiesList } from '@/features/cities/cities-list'
import { CitySearch, ICityOption } from '@/features/cities/city-search'
import { useRouter } from '@/setup/i18n/use-router'

interface IClientInteractiveCitiesSearchListProps {
    className?: string
}

export const ClientInteractiveCitiesSearchList = memo<IClientInteractiveCitiesSearchListProps>(
    ({ className }) => {
        const t = useTranslations()

        const router = useRouter()

        const { data } = useGetCitiesQuery({})

        const allCities = data?.data

        const countryCount = useMemo(() => {
            const allCountries = allCities?.reduce<ICountry[]>((acc, city) => {
                if (!city.country) return acc
                const isIncluded = acc.find((country) => country.slug === city.country?.slug)
                return isIncluded ? acc : [...acc, city.country]
            }, [])

            return allCountries?.length
        }, [allCities])

        const onCitySelect = useCallback(
            (city: ICity) => {
                if (!city) return Promise.reject()

                router.push({
                    pattern: '/restaurants/[citySlug]',
                    params: { citySlug: city.slug },
                })
            },
            [router]
        )

        const onCityOptionSelect = useCallback(
            (cityOption: ICityOption | null) => {
                if (!cityOption?.value) return

                onCitySelect(cityOption.value)
            },
            [onCitySelect]
        )

        return (
            <InteractiveCitiesSearchListContainer className={className}>
                <div className="flex flex-row mb-4 sticky">
                    <h2 className="mr-2 text-[20px] md:text-[38px]">{t('citySearch.title')}</h2>
                </div>

                <p className="block text-[14px] font-normal text-black/70 max-w-[500px] md:text-start mb-6">
                    {t('citySearch.description', {
                        cityCount: allCities?.length ?? 'x',
                        countryCount: countryCount ?? 'x',
                    })}
                </p>

                <CitySearch
                    autoFocus={false}
                    onSelect={onCityOptionSelect}
                    placeholder={t('citySearch.searchPlaceholder')}
                />

                <div className="mt-6" />

                <CitiesList />
            </InteractiveCitiesSearchListContainer>
        )
    }
)

ClientInteractiveCitiesSearchList.displayName = 'ClientInteractiveCitiesSearchList'
